export type WorkspaceNavigationGuard = () => Promise<boolean>;

let activeGuard: WorkspaceNavigationGuard | undefined;
let activeGuardHistoryIndex: number | undefined;
let pendingConfirmation: Promise<boolean> | undefined;

/** readHistoryIndex 读取 React Router 写入的历史序号，用于先恢复再确认浏览器返回。 */
function readHistoryIndex(browserWindow: Window): number | undefined {
  const index = Number(browserWindow.history.state?.idx);
  return Number.isFinite(index) ? index : undefined;
}

/** registerWorkspaceNavigationGuard 注册当前工作台页面唯一的异步离开门禁。 */
export function registerWorkspaceNavigationGuard(
  guard: WorkspaceNavigationGuard,
  historyIndex = readHistoryIndex(window),
) {
  activeGuard = guard;
  activeGuardHistoryIndex = historyIndex;
  pendingConfirmation = undefined;
  return () => {
    if (activeGuard !== guard) return;
    activeGuard = undefined;
    activeGuardHistoryIndex = undefined;
    pendingConfirmation = undefined;
  };
}

/** hasWorkspaceNavigationGuard 判断宿主导航是否需要等待业务页面保存。 */
export function hasWorkspaceNavigationGuard() {
  return Boolean(activeGuard);
}

/** confirmWorkspaceNavigation 串行复用同一次离开确认，避免连续点击触发并发保存。 */
export async function confirmWorkspaceNavigation(): Promise<boolean> {
  if (!activeGuard) return true;
  if (!pendingConfirmation) {
    const guard = activeGuard;
    pendingConfirmation = Promise.resolve()
      .then(() => guard())
      .catch(() => false)
      .finally(() => {
        pendingConfirmation = undefined;
      });
  }
  return pendingConfirmation;
}

/** createLatestWorkspaceNavigator 复用全局门禁，并确保并发点击只执行最后一次导航意图。 */
export function createLatestWorkspaceNavigator(navigate: (path: string) => void) {
  let navigationSequence = 0;
  /** navigateLatest 等待共享门禁后校验意图序号，旧点击不得覆盖用户的新选择。 */
  return async function navigateLatest(path: string): Promise<void> {
    navigationSequence += 1;
    const currentSequence = navigationSequence;
    const allowed = await confirmWorkspaceNavigation();
    if (!allowed || currentSequence !== navigationSequence) return;
    navigate(path);
  };
}

/** stopPopstate 阻止 React Router 消费尚未通过业务门禁的历史导航。 */
function stopPopstate(event: PopStateEvent) {
  event.preventDefault();
  event.stopImmediatePropagation();
}

/** installWorkspacePopstateGuard 在浏览器返回时先恢复当前页，确认保存后再重放原导航。 */
export function installWorkspacePopstateGuard(browserWindow: Window = window) {
  let allowNextPopstate = false;
  let handlingPopstate = false;
  let resolveRestoration: (() => void) | undefined;

  /** restoreActiveHistory 把已经移动的游标恢复到门禁页面，重复返回也不能留下地址错位。 */
  const restoreActiveHistory = (nextIndex: number) => {
    if (
      !Number.isFinite(nextIndex)
      || activeGuardHistoryIndex === undefined
      || nextIndex === activeGuardHistoryIndex
    ) return;
    browserWindow.history.go(activeGuardHistoryIndex - nextIndex);
  };

  /** handlePopstate 协调历史恢复、异步保存和最终导航三个阶段。 */
  const handlePopstate = (event: PopStateEvent) => {
    if (allowNextPopstate) {
      allowNextPopstate = false;
      return;
    }
    if (resolveRestoration) {
      stopPopstate(event);
      const nextIndex = Number(event.state?.idx);
      if (nextIndex !== activeGuardHistoryIndex) {
        restoreActiveHistory(nextIndex);
        return;
      }
      const resolve = resolveRestoration;
      resolveRestoration = undefined;
      resolve();
      return;
    }
    if (handlingPopstate) {
      stopPopstate(event);
      // 确认期间的再次返回已经改变浏览器游标，必须立即复原而不是只阻止路由监听器。
      restoreActiveHistory(Number(event.state?.idx));
      return;
    }
    if (!activeGuard || activeGuardHistoryIndex === undefined) return;
    const nextIndex = Number(event.state?.idx);
    if (!Number.isFinite(nextIndex)) return;
    const delta = nextIndex - activeGuardHistoryIndex;
    if (delta === 0) return;

    stopPopstate(event);
    handlingPopstate = true;
    const restored = new Promise<void>((resolve) => {
      resolveRestoration = resolve;
    });
    // popstate 发出时地址已经变化；先回到笔记页，避免保存确认期间卸载编辑器。
    browserWindow.history.go(-delta);
    void restored.then(async () => {
      const allowed = await confirmWorkspaceNavigation();
      handlingPopstate = false;
      if (!allowed) return;
      allowNextPopstate = true;
      browserWindow.history.go(delta);
    });
  };

  // 宿主路由器先于工作台页面注册冒泡监听器；捕获阶段保证门禁仍能在路由卸载页面前恢复历史项。
  browserWindow.addEventListener('popstate', handlePopstate, true);
  return () => browserWindow.removeEventListener('popstate', handlePopstate, true);
}
